import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api, apiClient, CommThread, CommThreadsParams } from '../api/client';
import { useNotifications } from '../context/NotificationContext';
import EventComments from '../components/messaging/EventComments';
import ConversationView from '../components/messaging/ConversationView';
import SearchableSelect from '../components/SearchableSelect';
import ToggleSwitch from '../components/forms/ToggleSwitch';
import Button from '../components/ui/Button';
import './CommunicationPage.css';

type TypeFilter = 'all' | 'event' | 'dm';
type Timeframe = 'upcoming' | 'recent' | 'past';

const DEFAULT_WINDOW = 60;
const OLDER_WINDOW = 365;
const SEARCH_DEBOUNCE_MS = 350;

// Conversation shape required by <ConversationView />. Fetched on DM selection.
interface DmConversation {
  _id: string;
  participants: { _id: string; name: string; email: string }[];
  lastMessage: { content: string; sender: string; timestamp: string } | null;
  updatedAt: string;
}

const CommunicationPage: React.FC = () => {
  const { t } = useTranslation();
  const { onNewMessage } = useNotifications();

  const [threads, setThreads] = useState<CommThread[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [timeframe, setTimeframe] = useState<Timeframe>('upcoming');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showOlder, setShowOlder] = useState(false);

  // Selection
  const [selected, setSelected] = useState<{ type: 'event' | 'dm'; refId: number } | null>(null);
  const [dmConversation, setDmConversation] = useState<DmConversation | null>(null);
  const [dmLoading, setDmLoading] = useState(false);
  // Tracks the most recently requested DM so an out-of-order fetch response
  // (rapid switching between threads) cannot render a stale conversation.
  const dmRequestRef = useRef<number | null>(null);

  // Debounce the search input.
  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const buildParams = useCallback((): CommThreadsParams => {
    const params: CommThreadsParams = {
      window: showOlder ? OLDER_WINDOW : DEFAULT_WINDOW,
    };
    if (typeFilter !== 'all') params.type = typeFilter;
    if (search.trim()) params.search = search.trim();
    if (unreadOnly) params.unreadOnly = true;
    // Timeframe is only meaningful for event-comment threads.
    if (typeFilter !== 'dm') params.timeframe = timeframe;
    return params;
  }, [typeFilter, search, unreadOnly, showOlder, timeframe]);

  const fetchThreads = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.communication.threads(buildParams());
      setThreads(data.threads);
    } catch (err) {
      console.error('Failed to fetch communication threads:', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Live updates: a new DM arrives → re-fetch so the thread bubbles to the top
  // with a refreshed snippet. If it is not the currently-open thread, bump its
  // unread count optimistically (the re-fetch reconciles authoritative counts).
  useEffect(() => {
    const unsubscribe = onNewMessage((message) => {
      const incomingRefId = Number(message.conversation);
      const isOpen =
        selected?.type === 'dm' && selected.refId === incomingRefId;
      if (!isOpen) {
        setThreads((prev) =>
          prev.map((thr) =>
            thr.type === 'dm' && thr.refId === incomingRefId
              ? { ...thr, unreadCount: thr.unreadCount + 1 }
              : thr
          )
        );
      }
      fetchThreads();
    });
    return unsubscribe;
  }, [onNewMessage, selected, fetchThreads]);

  const markThreadReadLocal = useCallback((type: 'event' | 'dm', refId: number) => {
    setThreads((prev) =>
      prev.map((thr) =>
        thr.type === type && thr.refId === refId ? { ...thr, unreadCount: 0 } : thr
      )
    );
  }, []);

  const handleSelect = useCallback(async (thread: CommThread) => {
    setSelected({ type: thread.type, refId: thread.refId });

    // Advance thread_reads + sync notification badge for this thread.
    markThreadReadLocal(thread.type, thread.refId);
    api.communication.markRead(thread.type, thread.refId).catch((err) => {
      console.error('Failed to mark thread read:', err);
    });

    // ConversationView needs the full conversation object — fetch it for DMs.
    if (thread.type === 'dm') {
      dmRequestRef.current = thread.refId;
      setDmConversation(null);
      setDmLoading(true);
      try {
        const { data } = await apiClient.get<{ conversation: DmConversation }>(
          `/api/messages/conversations/${thread.refId}`
        );
        // Ignore a response that lost the race to a newer selection.
        if (dmRequestRef.current !== thread.refId) return;
        setDmConversation(data.conversation);
      } catch (err) {
        console.error('Failed to load conversation:', err);
      } finally {
        if (dmRequestRef.current === thread.refId) setDmLoading(false);
      }
    } else {
      dmRequestRef.current = null;
      setDmConversation(null);
    }
  }, [markThreadReadLocal]);

  const formatRelativeTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('comments.justNow');
    if (minutes < 60) return t('comments.minutesAgo', { count: minutes });
    if (hours < 24) return t('comments.hoursAgo', { count: hours });
    if (days < 7) return t('comments.daysAgo', { count: days });
    return date.toLocaleDateString();
  };

  const typeOptions = useMemo(
    () => [
      { _id: 'all', name: t('communication.filters.all') },
      { _id: 'event', name: t('communication.filters.comments') },
      { _id: 'dm', name: t('communication.filters.dms') },
    ],
    [t]
  );

  const timeframeOptions = useMemo(
    () => [
      { _id: 'upcoming', name: t('communication.filters.timeframe.upcoming') },
      { _id: 'recent', name: t('communication.filters.timeframe.recent') },
      { _id: 'past', name: t('communication.filters.timeframe.past') },
    ],
    [t]
  );

  const renderDetail = () => {
    if (!selected) {
      return (
        <div className="comm-page__detail-empty">
          <p>{t('communication.empty.detail')}</p>
        </div>
      );
    }
    if (selected.type === 'event') {
      return <EventComments key={`event-${selected.refId}`} eventId={String(selected.refId)} />;
    }
    // DM detail.
    if (dmLoading || !dmConversation) {
      return (
        <div className="comm-page__detail-empty">
          <div className="comm-page__spinner" />
        </div>
      );
    }
    return <ConversationView key={`dm-${dmConversation._id}`} conversation={dmConversation} />;
  };

  return (
    <div className="comm-page">
      <h2 className="comm-page__title">{t('communication.title')}</h2>

      <div className="comm-page__layout">
        {/* LEFT: filter bar + thread list */}
        <aside className="comm-page__list-pane">
          <div className="comm-page__filters">
            <div className="comm-page__filter-row">
              <input
                type="text"
                className="comm-page__search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('communication.search.placeholder')}
                aria-label={t('communication.search.placeholder')}
              />
            </div>

            <div className="comm-page__filter-row comm-page__filter-row--selects">
              <div className="comm-page__filter-control">
                <SearchableSelect
                  id="comm-type-filter"
                  options={typeOptions}
                  value={typeFilter}
                  onChange={(val) => setTypeFilter((val as TypeFilter) || 'all')}
                  allowClear={false}
                />
              </div>
              {typeFilter !== 'dm' && (
                <div className="comm-page__filter-control">
                  <SearchableSelect
                    id="comm-timeframe-filter"
                    options={timeframeOptions}
                    value={timeframe}
                    onChange={(val) => setTimeframe((val as Timeframe) || 'upcoming')}
                    allowClear={false}
                  />
                </div>
              )}
            </div>

            <div className="comm-page__filter-row comm-page__filter-row--toggles">
              <ToggleSwitch
                checked={unreadOnly}
                onChange={setUnreadOnly}
                label={t('communication.filters.unreadOnly')}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOlder((prev) => !prev)}
                aria-pressed={showOlder}
              >
                {t('communication.filters.showOlder')}
              </Button>
            </div>
          </div>

          <div className="comm-page__threads">
            {loading ? (
              <div className="comm-page__threads-loading">
                <div className="comm-page__spinner" />
              </div>
            ) : threads.length === 0 ? (
              <p className="comm-page__threads-empty">{t('communication.empty.threads')}</p>
            ) : (
              threads.map((thread) => {
                const isActive =
                  selected?.type === thread.type && selected.refId === thread.refId;
                return (
                  <button
                    key={`${thread.type}-${thread.refId}`}
                    type="button"
                    className={`comm-thread${isActive ? ' comm-thread--active' : ''}${
                      thread.unreadCount > 0 ? ' comm-thread--unread' : ''
                    }`}
                    onClick={() => handleSelect(thread)}
                  >
                    <div className="comm-thread__top">
                      <span className="comm-thread__title">
                        {thread.title || t('communication.untitledConversation')}
                      </span>
                      <span className="comm-thread__time">
                        {thread.lastActivityAt
                          ? formatRelativeTime(thread.lastActivityAt)
                          : ''}
                      </span>
                    </div>
                    {thread.subtitle && (
                      <span className="comm-thread__subtitle">{thread.subtitle}</span>
                    )}
                    <div className="comm-thread__bottom">
                      {thread.snippet && (
                        <span className="comm-thread__snippet">{thread.snippet}</span>
                      )}
                      {thread.unreadCount > 0 && (
                        <span
                          className="comm-thread__badge"
                          aria-label={t('communication.unread', { count: thread.unreadCount })}
                        >
                          {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* RIGHT: detail pane */}
        <section className="comm-page__detail-pane">{renderDetail()}</section>
      </div>
    </div>
  );
};

export default CommunicationPage;
